import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportWebError } from '../../lib/error-reporter';
import { resolveApiRoot } from '../../lib/api-fetcher';

export interface BrandingData {
  companyName: string;
  logoUrl: string;
  logoIconUrl: string;
  brandColor: string;
}

interface BrandingContextValue {
  branding: BrandingData | null;
  isLoading: boolean;
}

const defaultContext: BrandingContextValue = {
  branding: null,
  isLoading: true,
};

const BrandingContext = createContext<BrandingContextValue>(defaultContext);

export function useBranding() {
  return useContext(BrandingContext);
}

async function fetchBranding(): Promise<BrandingData | null> {
  try {
    const apiRoot = resolveApiRoot();
    const res = await fetch(`${apiRoot}/public/branding`, {
      signal: AbortSignal.timeout(5000),
    });
    if (!res.ok) {
      reportWebError(new Error(`Branding fetch failed with status ${res.status}`));
      return null;
    }
    const data = (await res.json()) as BrandingData;
    return data.logoUrl || data.logoIconUrl || data.companyName ? data : null;
    // allow-silent-catch: reported via reportWebError; falls back to default branding since this is a cosmetic-only fetch, not worth a user-facing toast
  } catch (error) {
    reportWebError(error);
    return null;
  }
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { data: branding, isLoading } = useQuery({
    queryKey: ['branding'],
    queryFn: fetchBranding,
    staleTime: Infinity,
    retry: 1,
  });

  return (
    <BrandingContext.Provider value={{ branding: branding ?? null, isLoading }}>
      {children}
    </BrandingContext.Provider>
  );
}
